import React from 'react';
import {
  Outlet, NavLink, useLocation, useNavigate,
} from 'react-router-dom';
import {
  ArrowDownCircle, ArrowUpCircle, Gift, LogOut, Target, Wallet,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { getAccountMenuItems } from '../lib/accountMenu';
import { adminShortcutVisible } from '../lib/adminDomain';
import { useActiveTheme } from '../lib/activeTheme';
import ProgressFill from '../components/ProgressFill';
import VegaAccount from '../themes/vega/VegaAccount';
import {
  ACCOUNT_PAGE_ATTRIBUTE, HOOKS, accountHook, hook,
} from '../lib/domHooks';
import { accountPageKey } from '../lib/pageKey';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} ₺`;

// `children` hesap bölümünün dışındaki bir rotanın bu kabukta çizilmesi içindir
// (Betrun'da Promosyon Kodu, bkz. lib/accountMenu.js); verilmezse iç içe
// rotanın sayfası (`<Outlet />`) çizilir.
const AccountLayout = ({ children = null }) => {
  const {
    user, balance, bonusBalance, logout,
  } = useAuth();
  const settings = useSettings();
  const nav = useNavigate();
  // Vega bilinçli bir istisnadır: referans arayüzünde hesap sayfası sol menü
  // değil **kart ızgarası** düzenindedir ve fark renk/köşe değil düzenin
  // kendisidir (Herkül'ün spor sayfasındaki istisnanın aynısı). Giriş
  // denetimi ortak kalır — aşağıdaki `user` kontrolü ondan sonra gelir.
  const themeId = useActiveTheme();
  const { pathname } = useLocation();
  // Betrun'da para yatır/çek ekranı hesap menüsünü taşımaz: menüdeki diğer
  // maddeler zaten Profilim'den (ve sağ üstteki hesap menüsünden) açılıyor,
  // telefonda ızgara ödeme formunu ekranın altına itiyordu. Yerinde yalnızca
  // iki ekran arasında geçiş yapan düğmeler durur.
  const moneyPage = themeId === 'betrun'
    && /^\/hesap\/para-(yatir|cek)(\/|$)/.test(pathname);
  const wagerRequired = Number(user?.wager_required || 0);
  const wagerCompleted = Number(user?.wager_completed || 0);
  const wagerRemaining = Number(user?.wager_remaining || 0);
  const wagerProgress = wagerRequired > 0
    ? Math.min(100, (wagerCompleted / wagerRequired) * 100)
    : 100;

  // Panelden yazılan CSS'in tutunacağı kök (bkz. lib/domHooks.js →
  // `accountHook`): bölüm her sayfada aynı kimliği, alt sayfa kendi adını
  // taşır. Vega kendi düzenini çizer ve kökü orada verir.
  const rootHook = {
    ...hook(HOOKS.ACCOUNT),
    [ACCOUNT_PAGE_ATTRIBUTE]: accountPageKey(pathname),
  };

  if (!user) {
    return (
      <div className="pusu-body-gradient" {...rootHook}>
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <div className="pusu-panel max-w-md mx-auto p-8" {...accountHook('panel')}>
            <h2 className="text-xl font-bold gold-text mb-2">Giriş Gerekli</h2>
            <p className="text-sm text-gray-400 mb-5">Hesap sayfalarına erişmek için giriş yapmalısınız.</p>
            <button onClick={() => nav('/')} className="gold-btn px-5 py-2.5 rounded-xl">Anasayfaya Dön</button>
          </div>
        </div>
      </div>
    );
  }

  if (themeId === 'vega') return <VegaAccount>{children}</VegaAccount>;

  if (moneyPage) {
    return (
      <div className="pusu-body-gradient" {...rootHook}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-5 md:py-8">
          <nav className="account-money-switch" aria-label="Para işlemleri" {...accountHook('money-switch')}>
            <NavLink
              to="/hesap/para-yatir"
              {...accountHook('money-tab')}
              className={({ isActive }) => `account-money-tab ${isActive ? 'active' : ''}`}
            >
              <ArrowDownCircle size={18} /> Yatır
            </NavLink>
            <NavLink
              to="/hesap/para-cek"
              {...accountHook('money-tab')}
              className={({ isActive }) => `account-money-tab ${isActive ? 'active' : ''}`}
            >
              <ArrowUpCircle size={18} /> Çek
            </NavLink>
          </nav>
          <main className="min-w-0 mt-4" {...accountHook('content')}>
            {children || <Outlet />}
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="pusu-body-gradient" {...rootHook}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-5 md:py-8">
        <div className="grid grid-cols-12 gap-4">
          <aside className="account-sidebar col-span-12 md:col-span-3" {...accountHook('sidebar')}>
            <div className="pusu-panel p-3 sm:p-4" {...accountHook('panel')}>
              <div className="account-profile flex items-center gap-3 pb-3 sm:pb-4 border-b border-white/5" {...accountHook('user-card')}>
                {/* Renkler satır içi stilde değil sınıftadır (App.css → *Hesap
                    sayfaları*): satır içi stil panelden yazılan CSS'le ancak
                    `!important` ile ezilebiliyordu. */}
                <div className="account-avatar w-12 h-12 rounded-lg flex items-center justify-center font-black" {...accountHook('avatar')}>
                  {user.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate" {...accountHook('username')}>{user.username}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <div className="account-chip px-2 py-1 inline-flex items-center gap-1.5 rounded text-xs" {...accountHook('balance')}>
                      <Wallet size={12} className="text-[var(--pusu-gold)]" />
                      <span className="gold-text font-bold">{balance.toFixed(2)} ₺</span>
                    </div>
                    <div className="account-chip px-2 py-1 inline-flex items-center gap-1.5 rounded text-xs" {...accountHook('bonus-balance')}>
                      <Gift size={12} className="text-yellow-400" />
                      <span className="font-bold text-yellow-400">{bonusBalance.toFixed(2)} ₺ bonus</span>
                    </div>
                  </div>
                </div>
              </div>
              {wagerRequired > 0 && (
                <div className="account-wager-box mt-3 rounded-xl p-3" {...accountHook('wager')}>
                  <div className="flex items-center gap-2">
                    <Target size={14} className="text-amber-400" />
                    <span className="text-[11px] uppercase tracking-wider text-gray-400">Kalan Çevrim</span>
                    <span className="ml-auto text-sm font-black text-amber-400">
                      {formatMoney(wagerRemaining)}
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10" {...accountHook('progress')}>
                    <ProgressFill percent={wagerProgress} className="h-full rounded-full bg-amber-400 transition-all" />
                  </div>
                  <div className="mt-1.5 text-[11px] text-gray-500">
                    {formatMoney(wagerCompleted)} / {formatMoney(wagerRequired)} tamamlandı
                  </div>
                </div>
              )}
              <div className="account-nav mt-3 flex flex-col gap-1" {...accountHook('menu')}>
                {getAccountMenuItems(user, {
                  sportsEnabled: settings.sports_enabled !== false,
                  adminVisible: adminShortcutVisible(settings),
                  themeId,
                }).map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    // Şemanın CSS'i maddeyi adresinden tanır (Betrun para
                    // yatır/çek'i mobilde ızgaranın başına alır).
                    data-account-item={it.to}
                    {...accountHook('menu-item')}
                    className={({ isActive }) => `side-link ${it.admin ? 'border border-[var(--pusu-gold)]/25 bg-[var(--pusu-gold)]/5 text-[var(--pusu-accent-light)]' : ''} ${isActive ? 'active' : ''}`}
                  >
                    <it.icon size={16} />
                    <span>{it.label}</span>
                  </NavLink>
                ))}
                <button onClick={() => { logout(); nav('/'); }} className="side-link account-logout text-left mt-2 hover:!text-red-400" {...accountHook('logout')}>
                  <LogOut size={16} /> Çıkış Yap
                </button>
              </div>
            </div>
          </aside>
          <main className="col-span-12 min-w-0 md:col-span-9" {...accountHook('content')}>
            {children || <Outlet />}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AccountLayout;
